import { includes } from "lodash";
import { localDateTime } from "../../domain/local-date";
import { domain, label } from "../../common/dto/dto-annotation";
import { label50, label100 } from "../../domain/label";
import { OperationModeType, OperationStatusType } from "../constants/constants";
import { t } from "i18next";
import moment from "moment";

export type OperationValidationStatusType = "None" | "Valid" | "Invalid";

export interface IOperation {
    code: string;
    beginDate: string;
    endDate: string;
    status: OperationStatusType;
    modes: OperationModeType[];
    warehouseId: string;
    validationStatus: OperationValidationStatusType;
    validatedBy: string | null;
    validatedAt: moment.Moment | null;
}

export class Operation {
    private readonly _code: string;
    private readonly _beginDate: moment.Moment;
    private readonly _endDate: moment.Moment;
    private readonly _status: OperationStatusType;
    private readonly _modes: OperationModeType[];
    private readonly _warehouseId: string;
    private readonly _validationStatus: OperationValidationStatusType;
    private readonly _validatedBy: string | null;
    private readonly _validatedAt: moment.Moment | null;

    constructor(operation: IOperation) {
        this._code = operation.code;
        this._beginDate = moment(operation.beginDate);
        this._endDate = moment(operation.endDate);
        this._status = operation.status;
        this._modes = operation.modes;
        this._warehouseId = operation.warehouseId;
        this._validationStatus = operation.validationStatus;
        this._validatedBy = operation.validatedBy;
        this._validatedAt = operation.validatedAt;
    }

    @domain(label50)
    public get code() {
        return this._code;
    }

    @domain(localDateTime)
    public get beginDate() {
        return this._beginDate;
    }

    @domain(localDateTime)
    public get endDate() {
        return this._endDate;
    }

    @domain(label50)
    public get status() {
        return this._status;
    }

    public get modes() {
        return this._modes;
    }

    public get warehouseId() {
        return this._warehouseId;
    }

    @label("model.operation.validationStatus")
    public get validationStatusText() {
        return t(`model.validationStatus.${this._validationStatus}`);
    }

    @label("model.operation.validatedBy")
    public get validatedBy() {
        return this._validatedBy ?? "N/A";
    }

    @label("model.operation.validatedAt")
    public get validatedAt() {
        return this._validatedAt ? moment(this._validatedAt).format("YYYY-MM-DD HH:mm")  : "N/A";
    }

    @domain(label100)
    public get statusLabel() {
        return `${t(`model.operationStatus.${this.status}`)}`;
    }

    public get isBlueBrandMode(): boolean {
        return includes(this.modes, "BlueBrand");
    }

    public get isPalletMode(): boolean {
        return includes(this.modes, "PalletMode");
    }
}
